import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useRestaurant } from '../contexts/RestaurantContext';
import { base44 } from 'api/apiClient';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Building2,
  Users,
  TrendingUp,
  UserPlus,
  ArrowRight,
  Plus,
  Copy,
  Check,
  ChevronDown,
} from 'lucide-react';
import apiClient from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getApiClient() {
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };
  return {
    get: (url) => apiClient.get(`${API_URL}${url}`, { headers }).then(r => r.data),
    post: (url, data) => apiClient.post(`${API_URL}${url}`, data, { headers }).then(r => r.data),
  };
}

export default function OrgPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { switchRestaurant } = useRestaurant();

  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [showAddRestaurant, setShowAddRestaurant] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [inviteRestaurantId, setInviteRestaurantId] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);

  // Fetch org overview
  const { data: overview, isLoading } = useQuery({
    queryKey: ['org-overview'],
    queryFn: () => getApiClient().get('/organizations/overview/'),
  });

  // Create restaurant
  const createRestaurant = useMutation({
    mutationFn: (name) => getApiClient().post('/organizations/restaurants/', { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-overview'] });
      setNewRestaurantName('');
      setShowAddRestaurant(false);
    },
  });

  // Create invite
  const createInvite = useMutation({
    mutationFn: (data) => {
      const api = base44;
      // Use the raw apiClient to post to the invite endpoint
      const token = localStorage.getItem('access_token');
      return apiClient.post(`${API_URL}/auth/invites/`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.data);
    },
    onSuccess: (data) => {
      const frontendUrl = window.location.origin;
      const link = `${frontendUrl}/register/invite/${data.token}`;
      setInviteLink(link);
      setInviteEmail('');
    },
  });

  const handleOpenRestaurant = (restaurantId) => {
    switchRestaurant(restaurantId);
    navigate('/dashboard');
  };

  const handleCreateInvite = (e) => {
    e.preventDefault();
    const restaurantId = inviteRestaurantId || overview?.restaurants?.[0]?.id;
    if (!restaurantId) return;
    createInvite.mutate({
      email: inviteEmail || null,
      role: inviteRole,
      restaurant_id: restaurantId,
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const org = overview?.organization;
  const restaurants = overview?.restaurants || [];
  const members = overview?.members || [];

  const roleLabel = (role) => {
    const labels = {
      org_admin: t('orgPanel.roleOrgAdmin'),
      restaurant_admin: t('orgPanel.roleRestaurantAdmin'),
      staff: t('orgPanel.roleStaff'),
    };
    return labels[role] || role;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">
            {org?.name || t('orgPanel.title')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">{t('orgPanel.subtitle')}</p>
        </div>

        {/* Restaurant Cards */}
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">{t('orgPanel.yourRestaurants')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {restaurants.map((r) => (
              <Card key={r.id} className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 text-base mb-3">{r.name}</h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5" />
                        {t('orgPanel.todayRevenue')}
                      </span>
                      <span className="font-medium text-slate-900">
                        EUR {r.today_revenue?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">{t('orgPanel.todayGuests')}</span>
                      <span className="font-medium text-slate-900">{r.today_guests}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        {t('orgPanel.staffCount')}
                      </span>
                      <span className="font-medium text-slate-900">{r.staff_count}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => handleOpenRestaurant(r.id)}
                  >
                    {t('orgPanel.open')}
                    <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}

            {/* Add Restaurant Card */}
            {showAddRestaurant ? (
              <Card className="border border-dashed border-slate-300 shadow-sm">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-700 text-sm mb-3">{t('orgPanel.addRestaurant')}</h3>
                  <form onSubmit={(e) => { e.preventDefault(); if (newRestaurantName.trim()) createRestaurant.mutate(newRestaurantName.trim()); }}>
                    <Input
                      value={newRestaurantName}
                      onChange={(e) => setNewRestaurantName(e.target.value)}
                      placeholder={t('orgPanel.restaurantNamePlaceholder')}
                      className="mb-3"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" disabled={!newRestaurantName.trim() || createRestaurant.isPending}>
                        {createRestaurant.isPending ? '...' : t('orgPanel.create')}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowAddRestaurant(false)}>
                        {t('orgPanel.cancel')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <button
                onClick={() => setShowAddRestaurant(true)}
                className="border border-dashed border-slate-300 rounded-lg p-5 flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors min-h-[160px]"
              >
                <Plus className="w-6 h-6" />
                <span className="text-sm font-medium">{t('orgPanel.addRestaurant')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">
            {t('orgPanel.team')}
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            {t('orgPanel.teamCount', { count: members.length, locations: restaurants.length })}
          </p>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left px-4 py-3 font-medium text-slate-600">{t('orgPanel.memberName')}</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">{t('orgPanel.memberEmail')}</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">{t('orgPanel.memberRole')}</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">{t('orgPanel.memberRestaurant')}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {[m.first_name, m.last_name].filter(Boolean).join(' ') || m.username}
                        {m.id === user?.id && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{t('orgPanel.you')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{m.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          m.role === 'org_admin' ? 'bg-purple-100 text-purple-700' :
                          m.role === 'restaurant_admin' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {roleLabel(m.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{m.restaurant__name || '--'}</td>
                    </tr>
                  ))}
                  {members.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                        {t('orgPanel.noMembers')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Invite Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">{t('orgPanel.inviteTitle')}</h2>
          <p className="text-sm text-slate-500 mb-4">{t('orgPanel.inviteSubtitle')}</p>

          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <form onSubmit={handleCreateInvite} className="flex flex-col sm:flex-row gap-3">
                {restaurants.length > 1 && (
                  <div className="relative">
                    <select
                      value={inviteRestaurantId}
                      onChange={(e) => setInviteRestaurantId(e.target.value)}
                      className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {restaurants.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                )}
                <div className="relative">
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="staff">{t('orgPanel.roleStaff')}</option>
                    <option value="restaurant_admin">{t('orgPanel.roleRestaurantAdmin')}</option>
                    <option value="org_admin">{t('orgPanel.roleOrgAdmin')}</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={t('orgPanel.inviteEmailPlaceholder')}
                  className="flex-1"
                />
                <Button type="submit" disabled={createInvite.isPending}>
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  {createInvite.isPending ? '...' : t('orgPanel.generateInvite')}
                </Button>
              </form>

              {inviteLink && (
                <div className="mt-4 flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
                  <code className="flex-1 text-xs text-slate-600 break-all">{inviteLink}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex-shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
